import { computed, makeObservable } from 'mobx'
import type { MobxDocument } from '../firestore-mobx'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import {
  empty,
  updateRoomState,
  type FirestoreRoomState,
} from '../firestore/RoomState'
import type { FirebaseRepository } from './FirebaseRepository'
import { DateTime } from 'luxon'
import type { PartialWithFieldValue } from 'firebase/firestore'
import { serverTimestamp } from 'firebase/firestore'
import { assert } from '../firestore-mobx/utils'
import { AssignmentState, SectionState } from '../types'
import { type FirestoreSectionAssignment } from '../firestore/SectionAssignment'
import { stripSpecialChars } from '../util/stripSpecialCharacters'
import { ROOM_STATE_MAX_ALLOWED_USERS } from '../constants'
import { RoomStateStatus } from '../firestore/RoomState/types'

// The early-access buffer: how long before its scheduled time a room opens.
// Still the rule for demos — a rep-led demo can't begin without the rep, so the
// room has to be openable ahead of the guest — and for every room while the
// `enforceScheduledSessionStart` feature flag is off.
export const ROOM_CAN_START_MINUTES_BEFORE_SCHEDULED_AT = 15

// for backwards compatibility
export { RoomStateStatus }

export class RoomState extends ObservableModelWithDecorators<FirestoreRoomState> {
  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  static maxAllowedUsers = ROOM_STATE_MAX_ALLOWED_USERS

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreRoomState>
  ) {
    super(repository, doc)

    makeObservable(this)
  }

  @computed
  get users() {
    return this.repository.userStore.getUsers(this.userIds)
  }

  @computed
  get usersSortedByName() {
    return this.users
      .slice()
      .sort((a, b) => a.fullName.localeCompare(b.fullName))
  }

  @computed
  get groupLeaders() {
    return this.repository.userStore.getUsers(this.groupLeaderUserIds)
  }

  @computed
  get groupLeader() {
    return this.repository.userStore.getUser(this.groupLeaderUserId)
  }

  @computed
  get groupLeaderUserIds() {
    // Guard like userIds: a non-existent/deleted room streams with no data.
    return this.hasData ? this.data.groupLeaderUserIds : []
  }

  @computed
  get roomStateName(): string {
    if (!this.hasData) return ''
    if (this.data.roomStateName) return this.data.roomStateName
    return this.groupLeaders[0]?.fullName ?? ''
  }

  @computed
  get slideDeckId() {
    return this.hasData ? this.data.slideDeckId : ''
  }

  @computed
  get userIds() {
    // A non-existent/deleted room streams with no data; treat it as memberless
    // rather than throwing (the URL can point at a gone room we keep to show the
    // no-access pane).
    return this.hasData ? this.data.userIds : []
  }

  @computed
  get currentUserIsHidden(): boolean {
    return !!(
      this.hasData &&
      this.data.hiddenUserIds &&
      this.data.hiddenUserIds.includes(this.repository.uid)
    )
  }

  @computed
  get userCount() {
    return this.userIds.length
  }

  @computed
  get groupLeaderUserId() {
    return this.groupLeaderUserIds[0]
  }

  @computed
  get didStart() {
    return (
      typeof this.data.activeSlide === 'number' && this.data.activeSlide >= 0
    )
  }

  @computed
  get isScheduled() {
    return (this.data.scheduledAt || null) !== null
  }

  hasMaxUsers(
    groupingSizeMaximum: FirestoreSectionAssignment['groupingSizeMaximum']
  ) {
    const max = groupingSizeMaximum ?? RoomState.maxAllowedUsers
    return this.userCount >= max
  }

  @computed
  get isDemoCompleted() {
    assert(this.data.isDemo, 'isDemo must be true to invoke isDemoCompleted')
    const activeSlide = this.data.activeSlide
    const activeSlideChangedAt = this.data.activeSlideChangedAt || null
    const isFirstSlide = activeSlide === 0

    if (activeSlideChangedAt === null || !this.didStart) return false
    if (isFirstSlide && this.lastSlideChangeOver60MinutesAgo) return true
    if (!isFirstSlide && this.lastSlideChangeOver20MinutesAgo) return true
    return false
  }

  /**
   * Whether this room opens at its scheduled time rather than the usual
   * `ROOM_CAN_START_MINUTES_BEFORE_SCHEDULED_AT` minutes early (BL-4870).
   *
   * Controlled by the `enforceScheduledSessionStart` feature flag, so the old
   * and new timing can be swapped without a deploy. Demos are never enforced:
   * a rep-led demo can't begin without the rep, and solo demos have no
   * scheduled time at all.
   */
  @computed
  get scheduledStartEnforced() {
    if (this.data.isDemo) return false
    return this.repository.featureFlags.data.enforceScheduledSessionStart
  }

  /**
   * The instant this room becomes startable, or null when it isn't scheduled.
   *
   * Under enforcement the scheduled time is truncated to the minute, because
   * `repository.currentMinute` only ticks once a minute (just after :01): a
   * `scheduledAt` carrying nonzero seconds — which happens when it's set
   * programmatically to "now" rather than picked in the minute-precision UI —
   * would otherwise leave the join buttons disabled for most of a minute past
   * the start. Every surface shows these times to the minute, so the truncated
   * instant is the one the participant was shown.
   */
  @computed
  get canStartAt() {
    const scheduledAt = this.data.scheduledAt
    if (!scheduledAt) return null
    const scheduled = DateTime.fromJSDate(scheduledAt)
    if (this.scheduledStartEnforced) return scheduled.startOf('minute')
    return scheduled.minus({
      minutes: ROOM_CAN_START_MINUTES_BEFORE_SCHEDULED_AT,
    })
  }

  @computed
  get canStart() {
    const canStartAt = this.canStartAt
    if (!canStartAt) return false
    return this.repository.currentMinute > canStartAt
  }

  /**
   * strips punctuation and special characters from the room name
   * and returns a simple lower case string
   */
  @computed
  get simpleRoomName() {
    return stripSpecialChars(this.roomStateName).trim().toLowerCase()
  }

  get scheduledAtDate() {
    return this.data.scheduledAt
      ? DateTime.fromJSDate(this.data.scheduledAt)
      : undefined
  }

  get scheduledAtDateAsIsoShort() {
    if (!this.scheduledAtDate) return ''
    const meetingScheduledAtFormatted =
      this.scheduledAtDate.toISODate() +
      'T' +
      this.scheduledAtDate.toFormat('HH:mm')
    return meetingScheduledAtFormatted
  }

  getRoomStateStatus(
    expiresAt: Date | undefined,
    sectionState: SectionState,
    slideCount: number,
    assignmentState: AssignmentState,
    roomWasGraded: boolean | null,
    // When the viewing student has an individual deadline still in the future,
    // section completion must not expire them — their window legitimately
    // outlives the section. `expiresAt` should be the student's effective
    // deadline in that case.
    suppressSectionCompletedExpiry = false
  ) {
    const assignmentExpired =
      expiresAt && expiresAt.getTime() < new Date().getTime()

    const assignmentCancelled = assignmentState === AssignmentState.canceled

    const sectionCompleted =
      sectionState === SectionState.completed && !suppressSectionCompletedExpiry

    const expired = assignmentExpired || sectionCompleted

    const roomDidStart = typeof this.data.activeSlide === 'number'

    const onLastSlide =
      typeof this.data.activeSlide === 'number' &&
      this.data.activeSlide >= slideCount - 1

    if (!roomDidStart) {
      if (assignmentCancelled) return RoomStateStatus.canceled
      if (expired) return RoomStateStatus.expired
      if (this.data.scheduledAt) return RoomStateStatus.scheduled
      return RoomStateStatus.mustSchedule
    }

    // room start and on last slide or graded
    if (onLastSlide || roomWasGraded) return RoomStateStatus.completed

    // room start but not finished
    if (assignmentCancelled) return RoomStateStatus.canceled

    if (expired) {
      // if room started and assignment did expired, grading should kickoff on the cron
      // if the assignment has been expired for more than an hour, show abandoned as its apparent
      // the grading did not occur
      if (
        assignmentExpired &&
        DateTime.fromJSDate(expiresAt).plus({ hours: 1 }) < DateTime.now()
      )
        return RoomStateStatus.queuedForGrading

      return RoomStateStatus.abandoned
    }

    // after its been suspended for 2 hours and scheduled time is stale, change to mustSchedule
    // only enable this behavior for grouping. we want to be able to turn it off if it has unexpected side effects
    if (this.lastSlideChangeAndScheduledAtOver180MinutesAgo)
      return RoomStateStatus.mustScheduleSuspended

    // if not changed for an hour, session is suspended
    if (this.lastSlideChangeOver60MinutesAgo) return RoomStateStatus.suspended

    return RoomStateStatus.live
  }

  @computed
  get lastSlideChangeOver20MinutesAgo() {
    const activeSlideChangedAt = this.data.activeSlideChangedAt
    if (!activeSlideChangedAt) return false

    const delayCompleteByMinutes = 20

    const time20MinsAgo =
      activeSlideChangedAt.getTime() + 1000 * 60 * delayCompleteByMinutes

    return time20MinsAgo < this.repository.currentMinute.toMillis()
  }

  @computed
  get lastSlideChangeOver2MinutesAgo() {
    const activeSlideChangedAt = this.data.activeSlideChangedAt
    if (!activeSlideChangedAt) return false

    const delayCompleteByMinutes = 2

    const time20MinsAgo =
      activeSlideChangedAt.getTime() + 1000 * 60 * delayCompleteByMinutes

    return time20MinsAgo < this.repository.currentMinute.toMillis()
  }

  @computed
  get lastSlideChangeOver60MinutesAgo() {
    const activeSlideChangedAt = this.data.activeSlideChangedAt
    if (!activeSlideChangedAt) return false

    const delayCompleteByMinutes = 60

    const time60MinsAgo =
      activeSlideChangedAt.getTime() + 1000 * 60 * delayCompleteByMinutes

    return time60MinsAgo < this.repository.currentMinute.toMillis()
  }

  // True when the active slide last changed within the last 30 minutes. Reads
  // the reactive `currentMinute` clock so it flips to false on its own once the
  // window elapses (drives the End-of-Session "Rejoin" affordance).
  @computed
  get activeSlideChangedWithinLast30Minutes() {
    const activeSlideChangedAt = this.data.activeSlideChangedAt
    if (!activeSlideChangedAt) return false

    const windowEnds = activeSlideChangedAt.getTime() + 1000 * 60 * 30

    return windowEnds >= this.repository.currentMinute.toMillis()
  }

  @computed
  get lastSlideChangeAndScheduledAtOver180MinutesAgo() {
    const activeSlideChangedAt = this.data.activeSlideChangedAt
      ? DateTime.fromJSDate(this.data.activeSlideChangedAt)
      : null

    const scheduledAt = this.scheduledAtDate

    if (!activeSlideChangedAt || !scheduledAt) return false

    const threeHoursAgo = DateTime.now().minus({ hours: 3 })

    return activeSlideChangedAt < threeHoursAgo && scheduledAt < threeHoursAgo
  }

  /**
   * the server should clean these up automatically, but we can filter them out
   * to avoid showing them to the user in the interim
   * */
  @computed
  get isFlaggedForDeletion() {
    return this.data.deletable && this.userCount === 0 && !this.didStart
  }

  startRoomIfNotStarted = async () => {
    const activeSlide = this.data.activeSlide
    if (activeSlide === null || activeSlide === undefined) {
      const payload: PartialWithFieldValue<FirestoreRoomState> = {
        activeSlide: 0,
        activeSlideChangedAt: serverTimestamp(),
        roomStartedAt: serverTimestamp(),
      }

      // if we are not a group leader, make ourselves a a group leader
      if (!this.data.groupLeaderUserIds.includes(this.repository.uid)) {
        payload.groupLeaderUserIds = [this.repository.uid]
      }

      // we are in a room that is not started, start it and become the leader
      await updateRoomState(this.repository.firestore, this.id, payload)
    }
  }
}
